<template>
  <v-layout class="grey lighten-4" column>
    <div class="noto bold" width="100%">
      <div class="title primary--text">{{ section }}</div>
      <v-spacer></v-spacer>
      <v-dialog @keydown.esc="cancelDialog=false" v-model="cancelDialog" persistent max-width="400">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            rounded
            depressed
            small
            color="primary"
            dark
            v-bind="attrs"
            v-on="on"
          >
            Cancelar factura
          </v-btn>
        </template>
        <v-card>
          <v-card-title class="headline">¿Necesitas cancelar una factura generada da facturas de terceros?</v-card-title>
          <v-card-text>Si deseas cancelar una factura generada a partir de facturas de terceros, por favor contáctanos a <a href="mailto:facturas@eato.mx">facturas@eato.mx</a> y nosotros te apoyaremos en ese proceso.</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              rounded
              small
              depressed
              color="primary"
              @click="cancelDialog = false">Ok</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
    <v-flex xs12>
      <v-form
        v-if="provider.taxName && provider.taxId"
        v-model="form.isValid"
        ref="form">
        <v-row>
          <v-col md="12">
            <v-card class="px-3">
              <v-row>
                <v-col md="8">
                  <v-file-input
                    ref="xmlFile"
                    label="XML File"
                    accept=".xml"
                    required
                    hide-details
                    multiple
                    @change="addFile">
                  </v-file-input>
                </v-col>
                <v-col class="d-flex align-center justify-end">
                  <v-btn
                    color="primary"
                    rounded
                    small
                    depressed
                    :disabled="!areFiles"
                    @click="upload">Subir facturas</v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <div class="caption grey--text mb-2">
                    Archivos cargados:
                  </div>
                  <v-chip
                    v-for="(file, index) in xmlFiles"
                    :key="`file-${index}`"
                    class="mr-2"
                    color="primary"
                    small
                    close
                    @click:close="removeFile(index)">
                    {{ file.name }}
                  </v-chip>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col md="6">
            <v-card height="100%">
              <v-card-title>Emisor</v-card-title>
              <v-card-text>
                <div class="mb-4">Nombre: <b>{{ provider.taxName }}</b></div>
                <div>RFC: <b>{{ provider.taxId }}</b></div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col md="6">
            <v-card height="100%">
              <v-card-title>Datos para facturar</v-card-title>
              <v-card-text>
                <v-text-field
                  v-model="form.data.Folio"
                  dense
                  outlined
                  rounded
                  hide-selected
                  hide-no-data
                  label="Folio"
                  :rules="rules.required"/>
                <v-text-field
                  v-model="form.data.Receiver.Rfc"
                  dense
                  outlined
                  rounded
                  hide-selected
                  hide-no-data
                  label="RFC del receptor"
                  :rules="rules.required"/>
                <v-text-field
                  v-model="form.data.Receiver.Name"
                  dense
                  outlined
                  rounded
                  hide-selected
                  hide-no-data
                  :rules="rules.required"
                  label="Nombre Fiscal del receptor"/>
                <v-autocomplete
                  v-model="form.data.Receiver.CfdiUse"
                  :items="cfdiUses"
                  :filter="searchCfdiUse"
                  item-text="name"
                  item-value="satId"
                  dense
                  outlined
                  rounded
                  hide-selected
                  hide-no-data
                  :rules="rules.required"
                  label="Uso de CFDI">
                  <template v-slot:item="{ item }">
                    {{ item.satId }} | {{ item.name }}
                  </template>
                </v-autocomplete>
                <v-autocomplete
                  v-model="form.data.PaymentForm"
                  :items="paymentForms"
                  :filter="searchPaymentForm"
                  item-text="name"
                  item-value="satId"
                  dense
                  outlined
                  rounded
                  hide-selected
                  hide-no-data
                  :rules="rules.required"
                  label="Forma de pago">
                  <template v-slot:item="{ item }">
                    {{ item.satId }} | {{ item.name }}
                  </template>
                </v-autocomplete>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col md="12">
            <v-data-table
              :loading-text="table.title"
              :loading="table.loading"
              :headers="table.headers"
              :items="form.data.Items"
              hide-default-footer
              class="elevation-1">
              <template v-slot:top>
                <v-toolbar flat color="white" width="100%">
                  <v-toolbar-title>Productos</v-toolbar-title>
                </v-toolbar>
              </template>
              <template v-slot:item.Quantity="{ item }">
                {{ item.Quantity | toFixNumber(2) }}
              </template>
              <template v-slot:item.UnitCode="{ item }">
                {{ getUnit(item.UnitCode) }}
              </template>
              <template v-slot:item.UnitPrice="{ item }">
                {{ item.UnitPrice | toFixNumber(2) | prefix('$') }}
              </template>
              <template v-slot:item.Total="{ item }">
                {{ item.Total | toFixNumber(2) | prefix('$')  }}
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-form>

      <div
        v-else
        class="d-flex justify-center mt-6">
        <v-row
          align="center"
          justify="center">
          <v-icon
            size="150"
            color="primary">
            mdi-cogs
          </v-icon>
          <v-col cols="4">
            <div class="grey--text">
              <div
                class="title mb-3 font-weight-bold">
                Aún no has dado de alta tus datos fiscales.
              </div>
              <div class="subtitle-2">
                <div class="mb-3">
                  Ingresa los datos que te pedimos para que puedas generer facturas de sde aquí.
                </div>
                <div>
                  <router-link class="primary--text" to="/settings/provider">Ir a configurar mis datos fiscales</router-link>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>
    </v-flex>

    <v-dialog @keydown.esc="isOpen=false" v-model="isOpen" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">La factura se creó exitosamente</span>
        </v-card-title>
        <v-card-text>
          <div>
            Descarga la factura en el siguiente enlace:
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            small
            rounded
            color="primary"
            @click="isOpen = false">
            Cerrar
          </v-btn>
          <v-btn
            small
            rounded
            :href="downloadInvoice"
            target="_blank"
            color="primary">
            Descargar factura
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="overlay" z-index="999999999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-layout>
</template>
<script>
import { taxes, cfdiUses, paymentForms } from '@/utils/constants'
const { parseStringPromise } = require('xml2js')

export default {
  name: 'ReInvoice',
  data () {
    return {
      section: 'Re facturar',
      cancelDialog: false,
      overlay: false,
      isOpen: false,
      invoice: null,
      dialog: false,
      table: {
        title: 'Cargando productos',
        loading: false,
        headers: [
          { text: 'Insumo', value: 'Description' },
          { text: 'Cantidad', value: 'Quantity', sortable: false },
          { text: 'Unidad de medida', value: 'UnitCode', sortable: false },
          { text: 'Importe', value: 'UnitPrice', sortable: false },
          { text: 'Total', value: 'Total', sortable: false }
        ],
        options: {
          itemsPerPage: 15,
          page: 1
        }
      },
      taxes,
      cfdiUses,
      paymentForms,
      provider: {},
      cfdi: {},
      form: {
        isValid: false,
        data: {
          Folio: '',
          PaymentForm: '',
          Receiver: {
            Rfc: '',
            Name: '',
            CfdiUse: ''
          },
          Items: []
        },
        meta: {
          product: {}
        }
      },
      xmlFiles: [],
      rules: {
        required: [v => !!v || this.$t('validations.required')]
      }
    }
  },
  computed: {
    units: function () {
      return this.$store.state.login.units.data || []
    },
    areFiles () {
      return this.xmlFiles.length > 0
    },
    providerId () {
      return this.$store.state.session.user.provider
    },
    downloadInvoice () {
      return `${process.env.VUE_APP_API_URL}/v1/reinvoice/${this.cfdi.Id}?restaurant=${this.$store.state.session.restaurant}&access_token=${this.$store.state.session.token}`
    }
  },
  mounted: async function () {
    await this.get()
  },
  methods: {
    async get () {
      this.overlay = true
      try {
        const response = await this.$api.get(`/v1/providers/${this.providerId}/settings`)
        if (response) {
          this.provider = response
        }
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    getUnit (unitCode) {
      const unit = this.units.find(u => u.satCode && u.satCode === unitCode.toLowerCase())
      return unit
        ? unit.name
        : unitCode
    },
    searchCfdiUse (item, queryText, itemText) {
      const name = item.name.toLowerCase()
      const searchText = queryText.toLowerCase()
      return name.indexOf(searchText) >= 0
    },
    searchPaymentForm (item, queryText, itemText) {
      const name = item.name.toLowerCase()
      const searchText = queryText.toLowerCase()
      return name.indexOf(searchText) >= 0
    },
    removeFile (index) {
      this.xmlFiles.splice(index, 1)
      this.parseXML()
    },
    addFile (files) {
      if (files.length) {
        files.forEach(file => {
          this.xmlFiles.push(file)
        })
        this.parseXML()
      }
    },
    parseXML: async function () {
      const products = []
      const vm = this
      if (this.xmlFiles.length > 0) {
        for (const [index, file] of this.xmlFiles.entries()) {
          const reader = new FileReader()
          reader.readAsText(file)
          reader.onload = async function (e) {
            const invoice = await parseStringPromise(e.target.result)

            const productsInInvoice = invoice['cfdi:Comprobante']['cfdi:Conceptos'][0]['cfdi:Concepto'].map(function (concept) {
              const invoiceTaxes = concept['cfdi:Impuestos']
                ? concept['cfdi:Impuestos'][0]
                : null

              const translations = invoiceTaxes
                ? invoiceTaxes['cfdi:Traslados'][0]['cfdi:Traslado']
                : []

              const retentions = invoiceTaxes
                ? invoiceTaxes['cfdi:Retenciones']
                  ? invoiceTaxes['cfdi:Retenciones'][0]['cfdi:Retencion'].map(tax => {
                    tax.$.IsRetention = true
                    return tax
                  })
                  : []
                : []

              const taxes = translations.concat(retentions)
              const totalTaxes = taxes.reduce((acc, val) => {
                return acc + (val.$ ? Number(val.$.Importe) : 0)
              }, 0)
              const discount = concept.$.Descuento ? parseFloat(concept.$.Descuento).toFixed(2) : 0
              return {
                ProductCode: concept.$.ClaveProdServ,
                Description: concept.$.Descripcion,
                IdentificationNumber: concept.$.NoIdentificacion,
                Unit: concept.$.Unidad || concept.$.ClaveUnidad,
                UnitCode: concept.$.ClaveUnidad,
                UnitPrice: parseFloat(concept.$.ValorUnitario).toFixed(2),
                Quantity: parseFloat(concept.$.Cantidad).toFixed(2),
                Subtotal: parseFloat(concept.$.Importe).toFixed(2),
                Discount: discount,
                Taxes: taxes.map(taxInovice => {
                  const tax = vm.taxes.find(t => taxInovice.$.Impuesto === t.satId)
                  return {
                    Rate: parseFloat(taxInovice.$.TasaOCuota).toFixed(2),
                    Base: parseFloat(taxInovice.$.Base).toFixed(2),
                    Name: tax ? tax.name : '',
                    Total: parseFloat(taxInovice.$.Importe).toFixed(2),
                    IsRetention: taxInovice.$.IsRetention || false
                  }
                }),
                Total: (parseFloat(concept.$.Importe) - discount + totalTaxes).toFixed(2)
              }
            })
            products.push(...productsInInvoice)
          }
          reader.onloadend = function (e) {
            if (index === (this.xmlFiles.length - 1)) {
              this.form.data.Items = products
            }
          }.bind(this)
        }
      } else {
        this.form.data.Items = []
      }
    },
    upload: async function () {
      this.$refs.form.validate()
      if (this.form.isValid) {
        this.overlay = true
        try {
          const response = await this.$api.post(`/v1/reinvoice`, this.form.data)
          this.cfdi = response
          this.reset()
          this.isOpen = true
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.overlay = false
      }
    },
    reset () {
      this.form.data.Items = []
      this.form.data.Folio = ''
      this.xmlFiles = []
      this.$refs.form.reset()
    },
    updateItem (item) {
      item.total = item.quantityRequested * item.amount
    },
    close () {
      this.dialog = false
    },
    save () {
      this.dialog = false
    }
  }
}
</script>
